import { useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { useGetVeterinariansQuery } from '@/services/lookups/hooks';
import { TechnicianItem } from '@/services/lookups/types';

type Props = {
  value?: TechnicianItem | null;
  onChange?: (tech: TechnicianItem | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  paramKey?: string;
  validTechnicians?: string[];
};

export default function TechniciansDropdown({
  value,
  onChange,
  placeholder = 'Select technician',
  className,
  disabled,
  paramKey = 'otId',
  validTechnicians,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<TechnicianItem | null>(
    value ?? null,
  );

  const { data: allTechs = [], isLoading } = useGetVeterinariansQuery();

  const techs = useMemo(
    () =>
      validTechnicians?.length
        ? allTechs.filter((t) => validTechnicians.includes(t.id))
        : allTechs,
    [allTechs, validTechnicians],
  );

  useEffect(() => {
    if (value || selected || !techs.length) return;
    const idInUrl = searchParams.get(paramKey);
    if (!idInUrl) return;

    const found = techs.find((t) => t.id === idInUrl);
    if (found) {
      setSelected(found);
      onChange?.(found);
    }
  }, [techs, value, selected, paramKey]);

  useEffect(() => {
    if (!value) return;
    const current = searchParams.get(paramKey);
    if (current === value.id) return;

    const next = new URLSearchParams(searchParams);
    next.set(paramKey, value.id);
    setSearchParams(next, { replace: true });
    setSelected(value);
  }, [value, paramKey]);

  const commit = (tech: TechnicianItem | null) => {
    const next = new URLSearchParams(searchParams);
    if (tech) {
      next.set(paramKey, tech.id);
    } else {
      next.delete(paramKey);
    }
    setSearchParams(next, { replace: true });

    setSelected(tech);
    onChange?.(tech);
  };

  return (
    <Combobox
      items={techs}
      autoComplete="none"
      value={value ?? selected}
      onValueChange={(tech: TechnicianItem | null) => commit(tech)}
      itemToStringValue={(tech: TechnicianItem) => tech.id}
      itemToStringLabel={(tech: TechnicianItem) => tech.name}
      disabled={disabled}
    >
      <ComboboxInput
        className={className}
        placeholder={isLoading ? 'Loading...' : placeholder}
        showClear
      />
      <ComboboxContent>
        <ComboboxEmpty>No technicians found.</ComboboxEmpty>
        <ComboboxList>
          {(tech: TechnicianItem) => (
            <ComboboxItem key={tech.id} value={tech}>
              {tech.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
